import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Quote, TrendingDown, Clock, Zap, Shield, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import type { Stat, Testimonial } from '../types';

const Results: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const testimonials: Testimonial[] = [
    {
      quote: "GaadiMatic transformed our fleet management. We've cut operational costs by 18% in just 3 months.",
      author: "Rajesh Kumar",
      role: "Fleet Owner",
      company: "Kumar Transport",
      initials: "RK"
    },
    {
      quote: "The automated billing feature saved us 20 hours every week. No more manual invoices or payment delays.",
      author: "Priya Sharma",
      role: "Operations Manager",
      company: "Swift Logistics",
      initials: "PS"
    },
    {
      quote: "Real-time tracking gives us peace of mind. We always know where our vehicles are.",
      author: "Amit Patel",
      role: "Director",
      company: "Patel Movers",
      initials: "AP"
    }
  ];

  const nextSlide = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating, testimonials.length]);

  const prevSlide = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating, testimonials.length]);

  const goToSlide = (index: number) => {
    if (isAnimating || index === currentIndex) return;
    setIsAnimating(true);
    setCurrentIndex(index);
    setTimeout(() => setIsAnimating(false), 500);
  };

  useEffect(() => {
    intervalRef.current = setInterval(nextSlide, 5000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [nextSlide]);

  const stats: Stat[] = [
    { value: '15-20%', label: 'Cost Reduction', icon: TrendingDown },
    { value: '50%', label: 'Faster Billing', icon: Zap },
    { value: '8hrs', label: 'Time Saved Weekly', icon: Clock },
    { value: '99.9%', label: 'Compliance Rate', icon: Shield },
  ];

  return (
    <section className="py-20 bg-navy-950">
      <div className="container-main">
        <div className="text-center mb-16">
          <h2 className="text-h2 text-white mb-6 font-heading">
            Real Results from Real <span className="text-primary-400">Fleets</span>
          </h2>
          <p className="text-body-lg text-gray-400 max-w-2xl mx-auto">
            Join 500+ fleet operators who have transformed their operations with GaadiMatic FleetX.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {stats.map((stat, index) => (
            <div key={index} className="text-center p-6">
              <div className="w-12 h-12 bg-navy-900/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <stat.icon className="w-6 h-6 text-primary-400" />
              </div>
              <div className="text-4xl lg:text-5xl font-bold text-white font-heading">{stat.value}</div>
              <div className="text-gray-400">{stat.label}</div>
            </div>
          ))}
        </div>

        <div className="relative">
          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-x-1 disabled:opacity-50"
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="w-6 h-6 text-gray-600" />
          </button>

          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:translate-x-1 disabled:opacity-50"
            aria-label="Next testimonial"
          >
            <ChevronRight className="w-6 h-6 text-gray-600" />
          </button>

          <div className="overflow-hidden">
            <div 
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className="w-full flex-shrink-0 px-2"
                >
                  <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-gray-100 h-full">
                    <div className="flex gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                      ))}
                    </div>
                    <Quote className="w-10 h-10 text-primary-500 mb-6 flex-shrink-0" />
                    <blockquote className="text-lg text-white/90 leading-relaxed mb-4">
                      "{testimonial.quote}"
                    </blockquote>
                    <div className="flex items-center gap-4 mt-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-navy-900 to-navy-800 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                        <span className="text-xl">{testimonial.initials}</span>
                      </div>
                      <div>
                        <div className="text-white font-semibold">{testimonial.author}</div>
                        <div className="text-gray-400 text-sm">{testimonial.role}, {testimonial.company}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-center gap-2 mt-8">
        {testimonials.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`h-2 w-2 rounded-full transition-all duration-300 ${
              index === currentIndex 
                ? 'bg-primary-600 scale-110' 
                : 'bg-gray-300 hover:bg-gray-400'
            }`}
            aria-label={`Go to testimonial ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default Results;
